import React from 'react'
import {AsideDefault} from './components/aside/AsideDefault'
import {Footer} from './components/Footer'
import {HeaderWrapper} from './components/header/HeaderWrapper'
import {Toolbar} from './components/toolbar/Toolbar'
import {Content} from './components/Content'
import {MasterInit} from './MasterInit'
import {PageDataProvider} from './core'
import {
  DrawerMessenger,
  ActivityDrawer,
  Main,
  InviteUsers,
  UpgradePlan,
} from '../partials'
import { useCustomer } from '../../app/contexts/CustomerContext'
import { InactiveCustomerWarning } from '../../app/components/InactiveCustomerWarning'
import { SoftBlockWarning } from '../../app/components/SoftBlockWarning'
import { useNpsModal } from '../../app/hooks/use-nps-modal'
import { NpsSurveyModal } from '../../app/components/NpsSurveyModal'
import { Redirect, useLocation } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { getUserCustomerAccess, hasPermission, PERMISSIONS } from '../helpers/UserRoleHelpers'
import { PasswordExpiryAlert } from '../../app/components/PasswordExpiryAlert'
import { isOnboardingPublishedLocally } from '../../app/pages/onboarding/publishedFlag'

// Saídas do onboarding antes de publicar (D1): Cobrança e Convidar colega
const ONBOARDING_EXITS = ['/billing', '/users'];

const MasterLayout: React.FC = ({children}) => {
  const { customer, isInactive, isSoftBlocked } = useCustomer();
  const { showModal, npsQuestion, handleScoreClick, handleDismiss } = useNpsModal();
  const location = useLocation();
  const { user } = useAuth0();

  // Verificar se o usuário está na rota de billing
  const isBillingRoute = location.pathname.startsWith('/billing');
  
  // Verificar se o usuário tem permissão para acessar billing
  const currentCustomer = window.localStorage.getItem("activeCustomer") || "";
  const canAccessBilling = user && currentCustomer && hasPermission(user, currentCustomer, PERMISSIONS.MANAGE_SETTINGS);

  // Soft block (inadimplência): bloqueia o dashboard mas permite acesso ao billing
  if (isSoftBlocked) {
    if (isBillingRoute && canAccessBilling) {
      return (
        <PageDataProvider>
          <div className='d-flex flex-column flex-root'>
            <div className='page d-flex flex-row flex-column-fluid'>
              <AsideDefault />
              <div className='wrapper d-flex flex-column flex-row-fluid' id='kt_wrapper'>
                <HeaderWrapper />
                <div id='kt_content' className='content d-flex flex-column flex-column-fluid'>
                  <Toolbar />
                  <div className='post d-flex flex-column-fluid' id='kt_post'>
                    <Content>{children}</Content>
                  </div>
                </div>
                <Footer />
              </div>
            </div>
          </div>
          <ActivityDrawer />
          <DrawerMessenger />
          <Main />
          <InviteUsers />
          <UpgradePlan />
          <MasterInit />
        </PageDataProvider>
      );
    }

    return (
      <PageDataProvider>
        <SoftBlockWarning />
        <MasterInit />
      </PageDataProvider>
    );
  }

  // Onboarding: o wizard é tela cheia, sem o menu. Antes de publicar, o lojista só sai dele
  // para Cobrança e Usuários (D1); a equipe navega livre.
  const isOnboardingRoute = location.pathname.startsWith('/onboarding');
  if (isOnboardingRoute) {
    return (
      <PageDataProvider>
        {children}
        <MasterInit />
      </PageDataProvider>
    );
  }
  const isStaff = !!user && getUserCustomerAccess(user).isKonfidencyAdmin;
  const onboardingOpen =
    !isStaff &&
    !!customer?.onboarding &&
    customer.onboarding.status !== 'published' &&
    !isOnboardingPublishedLocally(currentCustomer);
  if (onboardingOpen && !ONBOARDING_EXITS.some((path) => location.pathname.startsWith(path))) {
    return <Redirect to='/onboarding' />;
  }

  // Se o cliente está inativo, verificar se é rota de billing antes de bloquear
  if (isInactive) {    // Permitir acesso à página de billing mesmo quando inativo (se tiver permissão)
    if (isBillingRoute && canAccessBilling) {
      // Renderizar layout normal para página de billing
      return (
        <PageDataProvider>
          <div className='d-flex flex-column flex-root'>
            <div className='page d-flex flex-row flex-column-fluid'>
              <AsideDefault />
              <div className='wrapper d-flex flex-column flex-row-fluid' id='kt_wrapper'>
                <HeaderWrapper />

                <div id='kt_content' className='content d-flex flex-column flex-column-fluid'>
                  <Toolbar />
                  <div className='post d-flex flex-column-fluid' id='kt_post'>
                    <Content>{children}</Content>
                  </div>
                </div>
                <Footer />
              </div>
            </div>
          </div>

          {/* begin:: Drawers */}
          <ActivityDrawer />
          <DrawerMessenger />
          {/* end:: Drawers */}

          {/* begin:: Modals */}
          <Main />
          <InviteUsers />
          <UpgradePlan />
          {/* end:: Modals */}

          <MasterInit />
        </PageDataProvider>
      );
    }
    
    // Caso contrário, mostrar tela de aviso
    return (
      <PageDataProvider>
        <InactiveCustomerWarning />
        <MasterInit />
      </PageDataProvider>
    );
  }

  return (
    <PageDataProvider>
      <div className='d-flex flex-column flex-root'>
        <div className='page d-flex flex-row flex-column-fluid'>
          <AsideDefault />
          <div className='wrapper d-flex flex-column flex-row-fluid' id='kt_wrapper'>
            <HeaderWrapper />

            <div id='kt_content' className='content d-flex flex-column flex-column-fluid'>
              <Toolbar />
              <div className='post d-flex flex-column-fluid' id='kt_post'>
                <Content>
                  <PasswordExpiryAlert />
                  {children}
                </Content>
              </div>
            </div>
            <Footer />
          </div>
        </div>
      </div>

      {/* begin:: Drawers */}
      <ActivityDrawer />
      <DrawerMessenger />
      {/* end:: Drawers */}

      {/* begin:: Modals */}
      <Main />
      <InviteUsers />
      <UpgradePlan />
      <NpsSurveyModal
        show={showModal}
        onScoreClick={handleScoreClick}
        onDismiss={handleDismiss}
        npsQuestion={npsQuestion}
      />
      {/* end:: Modals */}

      <MasterInit />
    </PageDataProvider>
  )
}

export {MasterLayout}
